import { useState, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CustomNotification } from 'common/components';

import * as minimumRequirementServices from 'services/minimumRequirements';

import * as productFullViewSelectors from 'pages/product-full-view/controllers/selectors';
import * as productFullViewActions from 'pages/product-full-view/controllers/actions';

import { getPropertiesParams } from 'utils/minimumRequirementUtils';

import {
  useDispatchReloadPage,
  useGetProductEnums,
  useGetProductFullView,
} from 'hooks';
import { useGetEntityProperties } from 'hooks/entityHooks';

import { additionalRequirementData } from 'static/Constants';

import { covertNestedArrToSingleArr } from 'pages/minimum-requirements/components/modal/utils';

import {
  filterMinimumRequirements,
  getErrorsForActiveProduct,
} from 'pages/home/ribbon/components/sections/product/components/make-product-active/utils';
import { PRIMARY_ERRORS_FOR_ACTIVE_PRODUCT } from 'static/productValidation';
import {
  addPrimaryErrorsToBasicModule,
  getAdditionalFieldNames,
  mapAdditionalProperties,
  mapRequiredProperties,
} from 'hooks/useProductStatusValidation';
import {
  useGetMinimumRequirementDetail,
  useUpdateUnmatchedRequirement,
} from 'common/components/product-add/make-product-active/hooks';

import { additionalRequirementFieldsLowercase } from 'common/components/product-add/make-product-active/constants';

import { ACTIVE_PRODUCT_VALIDATE_IMAGERY_ERRORS } from 'common/components/product-add/components/constants';

import { useQueryMarketingAssets } from 'pages/product-full-view/queries';
import { validateActiveProductKey } from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';

export const usePreviewRequirementForProduct = (props) => {
  const { visible, entityId, form, closeModal } = props;

  const queryClient = useQueryClient();

  const [additionalRequirements, setAdditionalRequirements] = useState([]);
  const [validateProduct, setValidateProduct] = useState(null);

  // Store all accumulated field values
  const [accumulatedValues, setAccumulatedValues] = useState({});

  const {
    validateProduct: initialValidateProduct,
    isFetching,
    handleRevalidateActiveProduct,
  } = usePreviewActiveProduct({
    productId: entityId,
    enabled: Boolean(entityId) && Boolean(visible),
  });

  const { productEnums } = useGetProductEnums();
  const dispatch = useDispatch();

  const productHierarchyInfo = useSelector(
    productFullViewSelectors.makeSelectCurrentHierarchy()
  );

  const reloadPage = useDispatchReloadPage();

  useEffect(() => {
    if (initialValidateProduct) {
      setValidateProduct(initialValidateProduct);
    }
  }, [initialValidateProduct]);

  const { entityProperties } =
    useGetEntityProperties({
      entityType: 'product',
    }) || [];

  const { handleRefetchProductFullView } = useGetProductFullView({
    productId: entityId,
    enabled: false,
  });

  const { entityRequirement } = useGetMinimumRequirementDetail({
    entityId,
    enabled: Boolean(entityId),
  });

  const unmatchedRequirementMutation = useUpdateUnmatchedRequirement();
  const validateActiveRequirement = useRevalidateActiveRequirement();

  const { invalidateMarketingAssets } = useQueryMarketingAssets({
    enabled: false,
  });

  const allProperties = useMemo(
    () => addPrimaryErrorsToBasicModule(entityProperties),
    [entityProperties]
  );

  const { properties: mappingProperties } = useMemo(
    () => covertNestedArrToSingleArr(allProperties),
    [allProperties]
  );

  const activeResult = useMemo(() => validateProduct ?? {}, [validateProduct]);

  const errorFields = useMemo(
    () => getErrorsForActiveProduct(activeResult),
    [activeResult]
  );

  const appliedTo = entityRequirement?.appliedTo;
  const isPrivateLabel = entityRequirement?.isPrivateLabel;

  const primaryErrors = useMemo(
    () => validateProduct?.errors ?? [],
    [validateProduct]
  );

  useEffect(() => {
    if (!mappingProperties || !productEnums) return;

    const productAdditionalRequirements = additionalRequirementData['Product'];
    const additionalFieldNames = getAdditionalFieldNames('Product');

    const mappedAdditionalProperties = mapAdditionalProperties(
      errorFields,
      productAdditionalRequirements,
      additionalFieldNames,
      entityRequirement
    );

    setAdditionalRequirements(mappedAdditionalProperties);
  }, [
    errorFields,
    productEnums,
    mappingProperties,
    entityRequirement,
    primaryErrors,
  ]);

  const handleValidateRequirements = async () => {
    try {
      const values = await form.getFieldsValue();

      const currentProperties = getPropertiesParams(values);

      // Merge with accumulated values (current values take precedence)
      const mergedPropertiesMap = mergePropertiesToMap(
        accumulatedValues,
        currentProperties
      );

      setAccumulatedValues(mergedPropertiesMap);

      const propertiesArray = Object.values(mergedPropertiesMap);

      const params = {
        productId: entityId,
        properties: propertiesArray,
      };

      validateActiveRequirement.mutate(params, {
        onSuccess: async (response) => {
          if (response?.isSuccess) {
            setValidateProduct(response?.data?.result);
            CustomNotification.success('Validate successfully');
          }
        },
        onError: (error) => {},
      });
    } catch (error) {}
  };

  const handleSubmitRequiredPropertiesForm = async () => {
    try {
      const values = await form.getFieldsValue();

      const currentProperties = getPropertiesParams(values);

      // Merge with all accumulated values for final submission
      const mergedPropertiesMap = mergePropertiesToMap(
        accumulatedValues,
        currentProperties
      );

      const propertiesArray = Object.values(mergedPropertiesMap);

      const requestParams = {
        entityId,
        entityName: 'PIM',
        properties: propertiesArray,
      };

      unmatchedRequirementMutation.mutate(requestParams, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            setAccumulatedValues({});
            handleRevalidateActiveProduct();
            handleRefetchProductFullView();
            invalidateMarketingAssets();
            queryClient.invalidateQueries({
              queryKey: validateActiveProductKey(),
            });
            dispatch(
              productFullViewActions.getProductImagery({
                hierarchyId: productHierarchyInfo?.productHierarchyId,
                productId: entityId,
              })
            );
            reloadPage();
            closeModal();
            CustomNotification.success('Submit successfully!');
          }
        },
        onError: (error) => {},
      });
    } catch (error) {}
  };

  const hasAdditionalRequirementsError = additionalRequirements.some(
    (requirementItem) => {
      return requirementItem.checkStatus === 'error';
    }
  );

  const minimumRequirementErrorFieldNames = useMemo(
    () =>
      filterMinimumRequirements(
        validateProduct?.minimumRequirementErrorFields ?? [],
        primaryErrors
      ),
    [primaryErrors, validateProduct]
  );

  const allRequiredFieldNames = useMemo(() => {
    return (
      sortFieldsWithImagesLast(validateProduct?.activeRequiredFields)?.map(
        (fieldName) => fieldName?.toLowerCase()
      ) ?? []
    );
  }, [validateProduct]);

  const additionalFieldNames = Object.keys(
    additionalRequirementFieldsLowercase
  );

  const allRequiredProperties = useMemo(() => {
    return mapRequiredProperties(
      allRequiredFieldNames,
      errorFields,
      mappingProperties,
      productEnums,
      [],
      primaryErrors
    );
  }, [
    allRequiredFieldNames,
    errorFields,
    mappingProperties,
    primaryErrors,
    productEnums,
  ]);

  const allAdditionalRequirements = useMemo(() => {
    return validateProduct?.additionalRequirements ?? {};
  }, [validateProduct]);

  return {
    fetchDataLoading: isFetching || unmatchedRequirementMutation.isLoading,
    appliedTo,
    isPrivateLabel,
    additionalRequirements,
    primaryErrors,
    minimumRequirementErrorFieldNames,
    validateLoading: validateActiveRequirement.isLoading,
    validateRequirements: handleValidateRequirements,
    onSubmitRequiredPropertiesForm: handleSubmitRequiredPropertiesForm,
    submitLoading: unmatchedRequirementMutation.isLoading,
    submitDisabled: unmatchedRequirementMutation.isLoading,
    hasAdditionalRequirementsError,
    allRequiredFieldNames,
    additionalFieldNames,
    allRequiredProperties,
    allAdditionalRequirements,
  };
};

export const previewActiveProductKey = (productId) => {
  return ['pim', 'validate-active-product', parseInt(productId)];
};

export const usePreviewActiveProduct = ({ productId, ...options }) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: previewActiveProductKey(productId),
    queryFn: async () => {
      return await minimumRequirementServices.revalidateActiveRequirements({
        productId,
      });
    },
    retry: 0,
    staleTime: 0,
    ...options,
  });
  const handleRevalidateActiveProduct = () => {
    queryClient.invalidateQueries({
      queryKey: previewActiveProductKey(productId),
    });
  };

  return {
    validateProduct: useMemo(
      () => query?.data?.data?.result ?? null,
      [query?.data]
    ),
    handleRevalidateActiveProduct,
    ...query,
  };
};

const sortFieldsWithImagesLast = (fields) => {
  if (!fields || !fields?.length) return [];

  const imageFields = Object.keys(ACTIVE_PRODUCT_VALIDATE_IMAGERY_ERRORS).map(
    (field) => field.toLowerCase()
  );

  return fields.sort((a, b) => {
    const aIsImage = imageFields.includes(a?.toLowerCase());
    const bIsImage = imageFields.includes(b?.toLowerCase());

    // If both are images or both are not images, maintain original order
    if (aIsImage === bIsImage) {
      return 0;
    }

    // If 'a' is an image and 'b' is not, move 'a' after 'b'
    if (aIsImage) {
      return 1;
    }

    // If 'b' is an image and 'a' is not, move 'a' before 'b'
    return -1;
  });
};

const mergePropertiesToMap = (accumulatedPropertiesMap, newPropertiesArray) => {
  // Start with accumulated properties
  const merged = { ...accumulatedPropertiesMap };

  // Add/override with new properties
  newPropertiesArray.forEach((prop) => {
    if (prop.fieldName) {
      merged[prop.fieldName] = prop;
    }
  });

  return merged;
};

export const useRevalidateActiveRequirement = () => {
  return useMutation({
    mutationFn: minimumRequirementServices.revalidateActiveRequirements,
  });
};

export const getDefaultPrimaryErrors = () => {
  return Object.entries(PRIMARY_ERRORS_FOR_ACTIVE_PRODUCT).map(
    ([key, value]) => ({
      key,
      ...value,
    })
  );
};
